/**
 * Undo Actions Panel
 * 
 * Collapsible panel showing recent undo-able document edits.
 * Serves as an audit trail for AI and manual edits with version restore capabilities.
 */

import { useState, useEffect, useCallback } from 'react';
import { documentVersionApi } from '../../services/api';
import { Badge, Button, Spinner, EmptyState } from '@/components/ui';
import { logger } from '../../utils/logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UndoHistoryEntry {
  entryId: string;
  source: 'ai' | 'manual';
  description: string;
  timestamp: string;
  affectedSections: string[];
  preAIVersionId?: string;
}

export interface UndoActionsPanelProps {
  projectId: string;
  documentNumber: string;
  isOpen: boolean;
  onClose: () => void;
  onUndoComplete: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UndoActionsPanel({
  projectId,
  documentNumber,
  isOpen,
  onClose,
  onUndoComplete,
}: UndoActionsPanelProps) {
  const [entries, setEntries] = useState<UndoHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  // Fetch undo history from backend
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await documentVersionApi.undoHistory(projectId, documentNumber);
      setEntries(data.entries || []);
    } catch (err: unknown) {
      logger.error('[UndoActionsPanel] Failed to fetch undo history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load undo history');
    } finally {
      setLoading(false);
    }
  }, [projectId, documentNumber]);

  // Fetch when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchHistory();
    }
  }, [isOpen, fetchHistory]);

  // Restore to a pre-edit version
  const handleRestore = async (entry: UndoHistoryEntry) => {
    if (!entry.preAIVersionId) return;

    try {
      setRestoringId(entry.entryId);
      await documentVersionApi.restoreVersion(projectId, documentNumber, entry.preAIVersionId);
      onUndoComplete();
    } catch (err: unknown) {
      logger.error('[UndoActionsPanel] Failed to restore version:', err);
      setError(err instanceof Error ? err.message : 'Failed to restore version');
    } finally {
      setRestoringId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute top-full right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 w-96 max-h-96 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted rounded-t-lg flex-shrink-0">
        <h3 className="text-sm font-semibold text-foreground">Recent Edits</h3>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground text-lg leading-none"
          title="Close"
        >
          ×
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="sm" className="mr-2" />
            <span className="text-sm text-muted-foreground">Loading history...</span>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="p-4">
            <p className="text-sm text-destructive mb-3">{error}</p>
            <Button variant="secondary" size="sm" onClick={fetchHistory}>
              Retry
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && entries.length === 0 && (
          <EmptyState
            title="No recent edits"
            description="No recent edits to undo"
            compact
          />
        )}

        {/* Entries list */}
        {!loading && !error && entries.length > 0 && (
          <div className="divide-y divide-border">
            {entries.map((entry) => (
              <div key={entry.entryId} className="px-4 py-3 hover:bg-muted transition-colors">
                {/* Top row: source badge, relative time, undo button */}
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Badge
                      variant={entry.source === 'ai' ? 'info' : 'secondary'}
                      className="flex-shrink-0"
                    >
                      {entry.source === 'ai' ? 'AI' : 'Manual'}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {getRelativeTime(entry.timestamp)}
                    </span>
                  </div>
                  {entry.preAIVersionId && (
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => handleRestore(entry)}
                      disabled={restoringId === entry.entryId}
                      className="flex-shrink-0"
                    >
                      {restoringId === entry.entryId ? 'Restoring...' : 'Undo'}
                    </Button>
                  )}
                </div>

                {/* Description */}
                <p className="text-sm text-foreground mb-1">{entry.description}</p>

                {/* Affected sections */}
                {entry.affectedSections.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {entry.affectedSections.map((section) => (
                      <span
                        key={section}
                        className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded"
                      >
                        {section}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer hint */}
      {!loading && !error && entries.length > 0 && (
        <div className="flex-shrink-0 border-t border-border px-4 py-2">
          <p className="text-xs text-muted-foreground">
            Use the Undo dropdown for time-based restore
          </p>
        </div>
      )}
    </div>
  );
}
